<script lang="ts">
  import type { ImportedCity, InventoryItem, ResolvedBuilding } from '$lib/types'
  import { resolveBuilding } from '$lib/catalogue'

  interface Props {
    inventory: InventoryItem[]
    importedCity: ImportedCity | null
    onPickup: (item: InventoryItem, resolved: ResolvedBuilding | null) => void
    /**
     * Permanently remove inventory items. The tab manages the
     * confirmation dialog itself (with stack-aware "Remove 1" /
     * "Remove all" buttons); the handler just dispatches.
     *
     *   action === 'one'  → remove only items[0]
     *   action === 'all'  → remove every item in items
     */
    onDiscard: (items: InventoryItem[], label: string, action: 'one' | 'all') => void
  }

  const { inventory, importedCity, onPickup, onDiscard }: Props = $props()

  let query = $state('')

  interface Row {
    /** Stable unique identifier across renders. For non-GB stacks, the
     *  cityentityId; for GBs (each its own row), the inventory id of the
     *  first item. Drives the {#each} keyed block — two GBs of the same
     *  name and level share `sortKey` but never share `key`. */
    key: string
    label: string
    type: string
    items: InventoryItem[]
    resolved: ResolvedBuilding | null
    sortKey: string
  }

  /**
   * Stack non-GB items by `cityentityId`. GBs stay individual rows so the
   * level on each can be read at a glance and dragged independently.
   */
  const rows = $derived.by<Row[]>(() => {
    const buckets = new Map<string, Row>()
    for (const item of inventory) {
      const resolved = resolveBuilding(item.cityentityId, importedCity ?? undefined)
      const isGb = resolved?.type === 'greatbuilding'
      const key = isGb ? `gb:${item.id}` : `entity:${item.cityentityId}`
      const existing = buckets.get(key)
      if (existing) {
        existing.items.push(item)
        continue
      }
      const baseLabel = resolved?.name ?? item.cityentityId
      const label =
        isGb && typeof item.level === 'number' ? `${baseLabel} · level ${item.level}` : baseLabel
      buckets.set(key, {
        key,
        label,
        type: resolved?.type ?? 'unknown',
        items: [item],
        resolved,
        sortKey: `${baseLabel.toLowerCase()}-${item.cityentityId}`,
      })
    }
    return Array.from(buckets.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey))
  })

  const filteredRows = $derived.by<Row[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => r.label.toLowerCase().includes(q))
  })

  // Discard dialog state. Holds the row's items + label while the player
  // chooses Remove 1 / Remove all / Cancel. Stacked rows show both Remove
  // buttons; single-item rows just show "Remove".
  let discardDialog: HTMLDialogElement | undefined = $state()
  let pending: { items: InventoryItem[]; label: string } | null = $state(null)

  function openDiscard(row: Row) {
    pending = { items: row.items, label: row.label }
    discardDialog?.showModal()
  }

  function closeDiscard() {
    discardDialog?.close()
    pending = null
  }

  function confirmDiscard(action: 'one' | 'all') {
    if (!pending) return
    onDiscard(pending.items, pending.label, action)
    closeDiscard()
  }

  function onDialogCancel(ev: Event) {
    // <dialog> "cancel" event fires on Esc; treat as plain close.
    ev.preventDefault()
    closeDiscard()
  }
</script>

{#if inventory.length === 0}
  <div class="flex flex-col items-center gap-2 px-4 py-8 text-center text-text-secondary">
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      class="text-text-muted"
    >
      <path d="M3 7h18l-2 13H5L3 7z" />
      <path d="M8 7V5a4 4 0 0 1 8 0v2" />
    </svg>
    <p class="text-xs">Your inventory is empty.</p>
    <p class="text-[11px] text-text-muted">
      Remove buildings from the city — they'll land here, ready to place again.
    </p>
  </div>
{:else}
  <div class="flex flex-col gap-2 p-2">
    <input
      type="search"
      bind:value={query}
      class="rounded border border-input-border bg-input-bg px-2 py-1 text-sm text-input-text placeholder-input-placeholder focus:border-accent focus:outline-none"
      placeholder="Search inventory…"
    />

    {#if filteredRows.length === 0}
      <p class="px-1 py-2 text-xs text-text-secondary">No items match.</p>
    {:else}
      <ul class="flex flex-col gap-1 text-sm">
        {#each filteredRows as row (row.key)}
      <li
        class="flex items-center justify-between gap-2 rounded border border-border-rule bg-surface-card-recessed px-2 py-1.5 hover:border-accent/50"
      >
        <div class="min-w-0 flex-1">
          <p class="truncate text-text-primary">{row.label}</p>
          {#if row.resolved}
            <p class="text-[11px] text-text-muted">
              {row.resolved.width}×{row.resolved.height} · {row.type}
            </p>
          {:else}
            <p class="text-[11px] text-text-muted">unknown footprint</p>
          {/if}
        </div>
        <div class="flex items-center gap-2">
          {#if row.items.length > 1}
            <span class="text-xs text-text-secondary">×{row.items.length}</span>
          {/if}
          <button
            type="button"
            class="rounded bg-accent px-2 py-1 text-[11px] text-white hover:bg-accent-bright"
            onclick={() => onPickup(row.items[0], row.resolved)}
            title="Click, then click on the grid to place"
          >
            place
          </button>
          <button
            type="button"
            class="rounded p-1 text-text-muted hover:bg-status-neg/20 hover:text-status-neg"
            onclick={() => openDiscard(row)}
            title="Permanently remove from inventory"
            aria-label="Discard from inventory"
          >
            ✕
          </button>
        </div>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

<dialog
  bind:this={discardDialog}
  oncancel={onDialogCancel}
  class="w-[min(420px,90vw)] rounded-xl border border-border-rule bg-surface-card p-0 text-text-primary backdrop:bg-black/60"
>
  {#if pending}
    {@const stackSize = pending.items.length}
    <form method="dialog" class="p-5" onsubmit={(e) => e.preventDefault()}>
      <h2 class="text-base font-semibold">
        Discard {pending.label}?
      </h2>
      <p class="mt-2 text-sm text-text-secondary">
        {#if stackSize > 1}
          You have {stackSize} of these. This cannot be undone via the in-game inventory.
        {:else}
          This cannot be undone via the in-game inventory.
        {/if}
      </p>
      <footer class="mt-5 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          class="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-card-recessed"
          onclick={closeDiscard}>Cancel</button
        >
        {#if stackSize > 1}
          <button
            type="button"
            class="rounded-md border border-status-neg/60 bg-status-neg/30 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-status-neg/55"
            onclick={() => confirmDiscard('one')}>Remove 1</button
          >
          <button
            type="button"
            class="rounded-md border border-status-neg bg-status-neg/60 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-status-neg/80"
            onclick={() => confirmDiscard('all')}>Remove all {stackSize}</button
          >
        {:else}
          <button
            type="button"
            class="rounded-md border border-status-neg bg-status-neg/60 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-status-neg/80"
            onclick={() => confirmDiscard('one')}>Remove</button
          >
        {/if}
      </footer>
    </form>
  {/if}
</dialog>

<style>
  dialog {
    margin: auto;
  }
</style>
